import type { AriaToastRegionProps } from '@react-aria/toast';
import { type QueuedToast, type ToastState, useToastQueue } from '@react-stately/toast';
import { useToastRegion } from '@react-aria/toast';
import { type ReactNode, useEffect, useState, useRef } from 'react';
import cx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { getGlobalToastQueue } from './toast-queue';
import type { ToastData } from './toast-component';
import { ToastComponent } from './toast-component';
import styles from './toast-context.module.css';

function useInitialAnimation(visibleToasts: QueuedToast<ToastData>[]): boolean {
  const [showInitial, setShowInitial] = useState(visibleToasts.length < 1);

  useEffect(() => {
    setShowInitial(visibleToasts.length < 1);
  }, [visibleToasts]);

  return showInitial;
}

export function ToastRegion(props: AriaToastRegionProps) {
  const state = useToastQueue<ToastData>(getGlobalToastQueue());
  const showInitial = useInitialAnimation(state.visibleToasts);

  return (
    <AnimatePresence>
      {state.visibleToasts.length > 0 && (
        <InnerToastRegion {...props} showInitial={showInitial} state={state} />
      )}
    </AnimatePresence>
  );
}

interface InnerToastRegionProps extends AriaToastRegionProps {
  showInitial?: boolean;
  state: ToastState<ToastData>;
}

function InnerToastRegion({
  showInitial = true,
  state,
  ...props
}: InnerToastRegionProps): ReactNode {
  const ref = useRef(null);
  const { regionProps } = useToastRegion(props, state, ref);

  return (
    <div {...regionProps} className={cx(styles.region, 'toast-region')} ref={ref}>
      {state.visibleToasts.map(toast => (
        <motion.div
          animate={{ opacity: 1, transform: 'translateX(0)' }}
          exit={{ opacity: 0, transform: 'translateX(-100%)' }}
          initial={showInitial ? { opacity: 0, transform: 'translateX(-100%)' } : undefined}
          key={toast.key}
          transition={{
            duration: 0.5,
            ease: 'easeOut',
          }}
        >
          <ToastComponent
            state={state}
            text={toast.content.text}
            toast={toast}
            type={toast.content.type}
          />
        </motion.div>
      ))}
    </div>
  );
}
